import { create } from 'zustand';

/**
 * Sidebar file-manager clipboard. Holds path(s) the user has cut or copied so
 * a subsequent paste knows what to act on. Kept here (Zustand) rather than in
 * the OS clipboard because we paste *paths*, not file contents — the OS
 * clipboard is for text/binary payloads, not filesystem refs.
 *
 * "Cut" rows are rendered dimmed in the tree until paste finishes (which
 * clears the clipboard) or until the user explicitly clears via Esc.
 */

export type ClipboardMode = 'cut' | 'copy';

export interface FsClipboardStore {
  /** Operation mode: `cut` means a paste moves; `copy` means a paste copies. */
  mode: ClipboardMode | null;
  /** Absolute source paths captured at cut/copy time. */
  paths: readonly string[];
  /** Replace the clipboard with a fresh `cut` of `paths`. */
  cut: (paths: string[]) => void;
  /** Replace the clipboard with a fresh `copy` of `paths`. */
  copy: (paths: string[]) => void;
  /** Empty the clipboard (called after successful paste or on Esc). */
  clear: () => void;
  /** Convenience: true when `path` is in the clipboard in 'cut' mode. */
  isCut: (path: string) => boolean;
}

export const useFsClipboardStore = create<FsClipboardStore>((set, get) => ({
  mode: null,
  paths: [],
  cut: (paths) => set({ mode: 'cut', paths: [...paths] }),
  copy: (paths) => set({ mode: 'copy', paths: [...paths] }),
  clear: () => set({ mode: null, paths: [] }),
  isCut: (path) => {
    const s = get();
    return s.mode === 'cut' && s.paths.includes(path);
  },
}));
